/**
 * In-memory session for placing checklist/document pins on the full Tegninger viewer.
 * Survives navigation and dialog unmounts (File objects stay in the module Map).
 */

const listeners = new Set();

/** @type {null | object} */
let activeSession = null;

/** Active viewer placement API (registered while DrawingViewer is mounted). */
let activeViewerBridge = null;

/** Avoid auto-open place-mode loops after the user cancels placement. */
const skipAutoOpenKeys = new Set();

function autoOpenKey(module, moduleParam, fileIndex) {
  return `${module}:${moduleParam}:${fileIndex}`;
}

export function shouldSkipAutoOpenPlacement(module, moduleParam, fileIndex) {
  return skipAutoOpenKeys.has(autoOpenKey(module, moduleParam, fileIndex));
}

export function clearSkipAutoOpenPlacement(module, moduleParam, fileIndex) {
  skipAutoOpenKeys.delete(autoOpenKey(module, moduleParam, fileIndex));
}

function notify() {
  // Snapshot the current session so all listeners on this tick
  // receive a consistent view, even if one listener mutates/consumes it.
  const snapshot = activeSession;
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch (err) {
      console.error("drawingDocumentPlacementSession listener error:", err);
    }
  });
}

export function subscribeDocumentPlacement(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getDocumentPlacementSession() {
  return activeSession;
}

/**
 * Register the live drawing viewer so checklist uploads can place on the open canvas
 * without navigating away.
 */
export function registerDrawingViewerPlacementBridge(bridge) {
  activeViewerBridge = bridge;
  return () => {
    if (activeViewerBridge === bridge) {
      activeViewerBridge = null;
    }
  };
}

export function getDrawingViewerPlacementBridge() {
  return activeViewerBridge;
}

/**
 * @param {object} input
 * @param {string|number} input.drawingId
 * @param {number} input.fileIndex
 * @param {Array} input.files - pending upload entries (includes File)
 * @param {string} input.module
 * @param {string|number} input.moduleParam
 * @param {boolean} [input.wantSketch]
 * @param {string|null} [input.returnPath]
 * @param {string|number|null} [input.suspendFillChecklistId]
 * @param {string|number|null} [input.suspendModuleDetailId]
 * @param {string|number|null} [input.suspendDeviationId]
 * @param {string} [input.title]
 * @param {string} [input.source] - e.g. "direct" | "document_manager"
 */
export function startDocumentPlacementSession(input) {
  const id = `doc-place-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  activeSession = {
    id,
    status: "placing",
    drawingId: input.drawingId,
    fileIndex: input.fileIndex,
    files: input.files,
    module: input.module,
    moduleParam: input.moduleParam,
    wantSketch: Boolean(input.wantSketch),
    returnPath: input.returnPath || null,
    suspendFillChecklistId: input.suspendFillChecklistId ?? null,
    suspendModuleDetailId: input.suspendModuleDetailId ?? null,
    suspendDeviationId: input.suspendDeviationId ?? null,
    title: input.title || "",
    source: input.source || "direct",
    result: null,
    createdAt: Date.now(),
  };
  notify();
  return activeSession;
}

export function updateDocumentPlacementSession(patch) {
  if (!activeSession) return null;
  activeSession = { ...activeSession, ...patch };
  notify();
  return activeSession;
}

export function completeDocumentPlacementSession(result) {
  if (!activeSession) return null;
  const files = Array.isArray(activeSession.files)
    ? activeSession.files.map((entry, index) => {
        if (index !== activeSession.fileIndex) return entry;
        return {
          ...entry,
          drawing_id: activeSession.drawingId,
          drawing_position_x: result.x,
          drawing_position_y: result.y,
          drawing_rotation: result.rotation || 0,
          drawing_page:
            typeof result.page === "number" ? result.page : entry.drawing_page ?? 0,
          drawing_version_id: result.versionId ?? entry.drawing_version_id ?? null,
          drawing_snapshot_blob: result.snapshotBlob || null,
          want_sketch: false,
        };
      })
    : activeSession.files;

  activeSession = {
    ...activeSession,
    status: "completed",
    result,
    files,
  };
  notify();
  return activeSession;
}

export function cancelDocumentPlacementSession() {
  if (!activeSession) return null;
  skipAutoOpenKeys.add(
    autoOpenKey(activeSession.module, activeSession.moduleParam, activeSession.fileIndex)
  );
  // Clear drawing_id/coords on the cancelled file so complete_upload does not
  // 422 with drawing_position_missing, and mark declined so defaultDrawing
  // does not re-bind the open drawing after restore.
  const fileIndex = activeSession.fileIndex;
  const files = Array.isArray(activeSession.files)
    ? activeSession.files.map((entry, index) => {
        if (index !== fileIndex) return entry;
        return {
          ...entry,
          drawing_id: null,
          drawing_position_x: null,
          drawing_position_y: null,
          drawing_rotation: 0,
          drawing_page: entry.drawing_page ?? null,
          drawing_version_id: null,
          drawing_snapshot_blob: null,
          drawing_pin_declined: true,
        };
      })
    : activeSession.files;

  activeSession = {
    ...activeSession,
    status: "cancelled",
    result: null,
    files,
  };
  notify();
  return activeSession;
}

/**
 * Consume a completed/cancelled session for a given upload target so the preview
 * dialog can be restored once. Returns null if nothing to restore.
 */
export function consumeDocumentPlacementRestore(module, moduleParam) {
  if (!activeSession) return null;
  if (activeSession.status !== "completed" && activeSession.status !== "cancelled") {
    return null;
  }
  if (
    String(activeSession.module) !== String(module) ||
    String(activeSession.moduleParam) !== String(moduleParam)
  ) {
    return null;
  }
  const snapshot = activeSession;
  activeSession = null;
  notify();
  return snapshot;
}

export function clearDocumentPlacementSession() {
  activeSession = null;
  notify();
}

/**
 * Start full-canvas placement: use open viewer if matching drawing, else navigate.
 * @returns {"viewer"|"navigate"|null}
 */
export function requestFullCanvasDocumentPlacement({
  drawingId,
  navigate,
  returnPath,
  ...sessionInput
}) {
  if (!drawingId) return null;

  const bridge = getDrawingViewerPlacementBridge();
  const sameViewer =
    bridge &&
    String(bridge.drawingId) === String(drawingId) &&
    typeof bridge.startPlacement === "function";

  // Stay on the open viewer — do not navigate back to the same route after place.
  const session = startDocumentPlacementSession({
    drawingId,
    returnPath: sameViewer ? null : returnPath || null,
    ...sessionInput,
  });

  if (sameViewer) {
    bridge.startPlacement(session);
    return "viewer";
  }

  if (typeof navigate === "function") {
    navigate(`/dashboard/drawings/${drawingId}`, {
      state: {
        documentPlacementSessionId: session.id,
        fromDocumentPlacement: true,
      },
    });
    return "navigate";
  }

  return null;
}
